'use es6';

import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContentRoutes from 'ContentUtils/Routes';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { undoModuleReducerChange, redoModuleReducerChange } from 'ContentEditorUI/redux/actions/undoRedoActions';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { getContentModelId, getSubcategory, getTemplatePath } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getThemeName, getHasTheme, getThemePath } from 'ContentEditorUI/redux/selectors/themeSelectors';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getCanRedoModules, getCanUndoModules } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getHasAutotranslateAccess, getIsUngatedForContentRemixQuickMix, getHasContentRemixWriteAccess } from 'ContentEditorUI/redux/selectors/authSelectors';
import { setScrollTo } from 'ContentEditorUI/redux/actions/baseSettingsActions';
import { openModal } from 'ContentEditorUI/redux/actions/appActions';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import { getHasDesignTabPermissions } from 'ContentEditorUI/redux/selectors/templateInfoSelectors';
import useFetchTheme from 'ContentEditorUI/hooks/useFetchTheme';
import { setShouldClearHeaderActiveItem } from 'ContentEditorUI/redux/actions/headerActions';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getContentAccessControlEditPermissionsDisabledTooltipI18nKey } from 'ui-memberships-settings-lib/utils/tooltips';
import { ContentTypes } from 'ui-memberships-settings-lib/constants/ContentTypes';
import { getUserHasManagePrivateContentAccess } from '../redux/selectors/authSelectors';
import { getHasReachedMlangBlogPostLimit } from '../redux/selectors/blogLimitsSelectors';
import { getShareablePreviewUrl } from '../redux/selectors/urlSelectors';
import { getUserCanViewBlogPostAccessControl, getCanUseAutotranslateForContentLanguage } from '../redux/selectors/postSettingsSelectors';
import NarrationFeatureBadge from '../components/audio/NarrationFeatureBadge';
import { useStartOnboardingTour } from '../components/onboarding/OnboardingTour';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { useDeepLSupportedLanguages } from 'ContentComponents/hooks/useDeepLSupportedLanguages';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const useNavigationGroups = () => {
  const dispatch = useDispatch();
  useFetchTheme();
  const templatePath = useSelector(getTemplatePath);
  const themePath = useSelector(getThemePath);
  const contentId = useSelector(getContentModelId);
  const categoryId = useSelector(getCategoryId);
  const subcategory = useSelector(getSubcategory);
  const isReadOnlyMode = useSelector(getIsReadOnlyMode);
  const canRedoModules = useSelector(getCanRedoModules);
  const canUndoModules = useSelector(getCanUndoModules);
  const hasReachedMlangBlogPostLimit = useSelector(getHasReachedMlangBlogPostLimit);
  const shareablePreviewUrl = useSelector(getShareablePreviewUrl);
  const themeName = useSelector(getThemeName);
  const hasTheme = useSelector(getHasTheme);
  const hasDesignTabPermissions = useSelector(getHasDesignTabPermissions);
  const {
    sourceLanguages
  } = useDeepLSupportedLanguages();
  const canUseAutotranslateForContentLanguage = useSelector(getCanUseAutotranslateForContentLanguage(sourceLanguages));
  const isUngatedForContentRemixQuickMix = useSelector(getIsUngatedForContentRemixQuickMix);
  const hasContentRemixWriteAccess = useSelector(getHasContentRemixWriteAccess);
  const hasAutotranslateAccess = useSelector(getHasAutotranslateAccess);
  const shouldDisableTheme = !hasTheme || !hasDesignTabPermissions;
  const userCanViewPostAccessControl = useSelector(getUserCanViewBlogPostAccessControl);
  const userHasManagePrivateContentAccess = useSelector(getUserHasManagePrivateContentAccess);
  const {
    openUpgradeModal: openAiTranslateUpgradeModal
  } = useUpgradeModal('blog-posts-direct-translate-ai-translate');
  const setSettingsModalOpenWithNavItem = useCallback(navItem => {
    dispatch(openModal(MODAL_TYPES.SETTINGS_MODAL));
    dispatch(setScrollTo(navItem));
  }, [dispatch]);
  const openDeleteModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.DELETE_MODAL));
  }, [dispatch]);
  const openCloneModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.CLONE_CONTENT_MODAL));
  }, [dispatch]);
  const openCreatePostModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.CREATE_CONTENT_MODAL));
  }, [dispatch]);
  const openContentRemixModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.CONTENT_REMIX));
    UsageTracker.trackEditorInteraction({
      action: 'Navigation Action',
      event: 'clicked-content-remix-modal'
    });
  }, [dispatch]);
  const openCreateMlangVariantModal = useCallback(() => {
    UsageTracker.trackEditorInteraction({
      action: 'Navigation Action',
      event: 'clicked-create-multi-lang-variation'
    });
    if (hasReachedMlangBlogPostLimit) {
      return dispatch(openModal(MODAL_TYPES.MLANG_LIMIT_UPGRADE_MODAL));
    }
    return dispatch(openModal(MODAL_TYPES.CREATE_MLANG_VARIANT));
  }, [dispatch, hasReachedMlangBlogPostLimit]);
  const openTranslateContentActionModal = useCallback(() => {
    dispatch(openModal(MODAL_TYPES.TRANSLATE_CONTENT_ACTION_MODAL));
    UsageTracker.trackEditorInteraction({
      action: 'Navigation Action',
      event: 'clicked-direct-translate'
    });
  }, [dispatch]);
  const onThemeClick = useCallback(() => {
    const params = {
      redirectUrl: window.location.pathname,
      fromAppName: 'blogEditor',
      templatePath
    };
    window.location = ContentRoutes.themeEditor(themePath, params);
  }, [templatePath, themePath]);
  const pageDetailsLink = useMemo(() => {
    return ContentRoutes.details(contentId, categoryId, subcategory);
  }, [categoryId, contentId, subcategory]);
  const startOnboardingTour = useStartOnboardingTour();
  const handleTakeTourClick = useCallback(() => {
    dispatch(setShouldClearHeaderActiveItem(true));
    startOnboardingTour();
  }, [dispatch, startOnboardingTour]);
  const mlangActionItems = useMemo(() => {
    const createTranslationAction = {
      id: 'createMlangVariant',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.createMlangVariant"
      }),
      onClick: openCreateMlangVariantModal
    };
    if (canUseAutotranslateForContentLanguage) {
      return [createTranslationAction, {
        id: 'translateContent',
        text: /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "editor.header.menus.file.translateContent"
          }), /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(AIBadge, {}), !hasAutotranslateAccess && /*#__PURE__*/_jsx(LockBadge, {})]
          })]
        }),
        onClick: hasAutotranslateAccess ? openTranslateContentActionModal : openAiTranslateUpgradeModal
      }];
    }
    return [createTranslationAction];
  }, [openCreateMlangVariantModal, canUseAutotranslateForContentLanguage, hasAutotranslateAccess, openTranslateContentActionModal, openAiTranslateUpgradeModal]);
  const groups = useMemo(() => [{
    id: 'file',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.file.label"
    }),
    children: [{
      id: 'new',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.createNew"
      }),
      nestedChildren: [{
        id: 'createPost',
        text: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.header.menus.file.createPost"
        }),
        onClick: openCreatePostModal
      }]
    }, {
      id: 'multiLanguage',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.multiLanguage"
      }),
      nestedChildren: mlangActionItems
    }, {
      id: 'clonePost',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.clone"
      }),
      onClick: openCloneModal
    }, {
      id: 'deletePost',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.delete"
      }),
      onClick: openDeleteModal
    }, ...(isUngatedForContentRemixQuickMix && hasContentRemixWriteAccess ? [{
      id: 'remixContent',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.file.remixContent"
      }),
      onClick: openContentRemixModal
    }] : [])]
  }, {
    id: 'edit',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.edit.label"
    }),
    children: [{
      id: 'undo',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.undo"
      }),
      onClick: () => dispatch(undoModuleReducerChange()),
      disableLink: !canUndoModules || isReadOnlyMode
    }, {
      id: 'redo',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.redo"
      }),
      onClick: () => dispatch(redoModuleReducerChange()),
      disableLink: !canRedoModules || isReadOnlyMode
    }, {
      id: 'editTheme',
      text: themeName ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.themeWithName",
        options: {
          themeName
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.edit.theme"
      }),
      disableLink: shouldDisableTheme,
      onClick: onThemeClick
    }]
  }, {
    id: 'view',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.view.label"
    }),
    children: [{
      id: 'versionHistory',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.view.versionHistory"
      }),
      onClick: () => dispatch(routeToPage('revisions'))
    }, {
      id: 'postDetails',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.view.details"
      }),
      href: pageDetailsLink,
      external: true
    }, {
      id: 'previewUrl',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.view.preview"
      }),
      href: shareablePreviewUrl,
      external: true,
      hideLink: !shareablePreviewUrl
    }]
  }, {
    id: 'settings',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.settings.label"
    }),
    children: [{
      id: 'general',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.general"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('general')
    }, {
      id: 'image',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.image"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('image')
    }, {
      id: 'audio',
      text: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "editor.header.menus.settings.audio"
        }), /*#__PURE__*/_jsx(NarrationFeatureBadge, {})]
      }),
      onClick: () => setSettingsModalOpenWithNavItem('audio')
    }, {
      id: 'metadata',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.metadata"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('metadata')
    }, {
      id: 'template',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.template"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('template')
    }, {
      id: 'langAndRegion',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.langAndRegion"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('langAndRegion')
    }, {
      id: 'accessControl',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "postSettings.patagoniaSettingsModal.accessControl.label"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('accessControl'),
      hideLink: !userCanViewPostAccessControl,
      disableLink: !userHasManagePrivateContentAccess,
      tooltipLabel: !userHasManagePrivateContentAccess ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: getContentAccessControlEditPermissionsDisabledTooltipI18nKey(ContentTypes.BLOG_POST)
      }) : null
    }, {
      id: 'advanced',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.settings.advanced"
      }),
      onClick: () => setSettingsModalOpenWithNavItem('advanced')
    }].filter(item => item)
  }, {
    id: 'help',
    text: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "editor.header.menus.help.label"
    }),
    children: [{
      id: 'takeTour',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.help.takeTour"
      }),
      onClick: handleTakeTourClick
    }, {
      id: 'knowledgeBase',
      text: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "editor.header.menus.help.knowledgeBase"
      }),
      onClick: () => window.open('https://knowledge.hubspot.com/website/topics#blog')
    }]
  }], [openCreatePostModal, mlangActionItems, openCloneModal, openDeleteModal, isUngatedForContentRemixQuickMix, hasContentRemixWriteAccess, openContentRemixModal, canUndoModules, isReadOnlyMode, canRedoModules, themeName, shouldDisableTheme, onThemeClick, pageDetailsLink, shareablePreviewUrl, userCanViewPostAccessControl, userHasManagePrivateContentAccess, handleTakeTourClick, dispatch, setSettingsModalOpenWithNavItem]);
  return groups;
};